import { MergeCellsOutlined } from '@ant-design/icons';
import { Button, Checkbox, Table, Tag, Row, Col, Input } from "antd";
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { trangThaiDangKyDaoTao, trangThaiLopDaoTao } from '../../constants';
import { Links } from '../../helpers/Links';
import ModalTextForm from "../../components/modal/ModalTextForm";
import NhanVienSelectBox from "../utils/NhanVienSelectBox";
import { useDispatch } from "react-redux";
import { hideLoadingScreen, showLoadingScreen } from "../../store/reducers/loading";
import { gopLopDangKyDaoTao, khoiTaoLopDaoTaoKeHoach } from "../../services/daotao";
import { toast } from "react-toastify";
import { Link } from "react-router-dom";
import TrangThaiLopDaoTaoTag from "../daotao/TrangThaiLopDaoTaoTag";

/**
 * Cấu hình hiển thị (màu + nhãn) cho từng trạng thái, dùng để đếm số lượng theo trạng thái
 */
const cauHinhTrangThai: Array<{ value: number; color: string; title: string }> = [
    { value: trangThaiDangKyDaoTao.khoiTao, color: "processing", title: "Khởi tạo" },
    { value: trangThaiDangKyDaoTao.thuHoi, color: "warning", title: "Thu hồi" },
    { value: trangThaiDangKyDaoTao.trinhLanhDaoPhong, color: "cyan", title: "Trình Lãnh đạo phòng" },
    { value: trangThaiDangKyDaoTao.lanhDaoPhongTuChoi, color: "error", title: "Lãnh đạo phòng từ chối" },
    { value: trangThaiDangKyDaoTao.duyetCapPhong, color: "success", title: "Lãnh đạo phòng đã duyệt" },
    { value: trangThaiDangKyDaoTao.chuyenVanPhong, color: "purple", title: "Chuyển văn phòng" },
    { value: trangThaiDangKyDaoTao.lanhDaoQuyTuChoi, color: "error", title: "Văn phòng từ chối" },
    { value: trangThaiDangKyDaoTao.pheDuyet, color: "success", title: "Duyệt" },
];

const mauPreset: Record<string, string> = {
    processing: "#1677ff",
    success: "#52c41a",
    error: "#ff4d4f",
    warning: "#faad14",
    cyan: "#13c2c2",
    purple: "#722ed1",
};

/**
 * Trạng thái hiển thị của 1 nhân viên trong lớp: ưu tiên trạng thái chi tiết đăng ký (nếu server trả về)
 */
const layTrangThai = (hocVien: any): number => hocVien.trangThaiChiTiet ?? hocVien.trangThai;

const layMauVaChuThichTrangThai = (hocVien: any): { mau?: string; chuThich?: string } => {
    // chưa đăng ký nhưng đã quá hạn kết thúc => coi như không đăng ký
    if (hocVien.ngayKetThuc && hocVien.trangThai === trangThaiDangKyDaoTao.chuaDangKy) {
        const ngayKT = new Date(hocVien.ngayKetThuc);
        const now = new Date();
        ngayKT.setHours(0, 0, 0, 0);
        now.setHours(0, 0, 0, 0);
        if (ngayKT < now) return { mau: mauPreset.warning, chuThich: "Không đăng ký" };
    }
    if (hocVien.trangThai === trangThaiDangKyDaoTao.chuaDangKy) {
        return { mau: mauPreset.warning, chuThich: "Chưa đăng ký" };
    }
    const cauHinh = cauHinhTrangThai.find((x) => x.value === layTrangThai(hocVien));
    if (!cauHinh) return {};
    return { mau: mauPreset[cauHinh.color], chuThich: cauHinh.title };
};

const getTrangThaiStats = (dsHocVien: Array<any>) => {
    const tags = cauHinhTrangThai
        .map((cauHinh) => {
            const soLuong = (dsHocVien || []).filter((hocVien) => layTrangThai(hocVien) === cauHinh.value).length;
            if (soLuong === 0) return null;
            return (
                <Tag color={cauHinh.color} key={cauHinh.value} title={cauHinh.title} style={{ margin: '2px' }}>
                    {soLuong}
                </Tag>
            );
        })
        .filter(Boolean);

    return tags.length > 0
        ? <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center' }}>{tags}</div>
        : "-";
};

/**
 * Mặc định chỉ những nhân viên đang ở trạng thái "Trình lãnh đạo phòng" mới được chọn (hiện checkbox)
 */
const choPhepChonMacDinh =(hocVien: any): boolean => hocVien.trangThai === trangThaiDangKyDaoTao.trinhLanhDaoPhong;

/**
 * Key của 1 nhân viên trong lớp (1 dòng chi tiết đăng ký)
 */
const layKeyHocVien = (hocVien: any): number => hocVien.idChiTiet > 0 ? hocVien.idChiTiet : hocVien.id;

/**
 * Lấy số tiền đầu tiên trong chuỗi dự trù kinh phí (vd: "5.000.000 đồng/người" => 5000000)
 */
const laySoTien = (text?: string): number | null => {
    const match = (text || '').match(/\d[\d.,]*/);
    if (!match) return null;
    const soTien = Number(match[0].replace(/[.,]/g, ''));
    return isNaN(soTien) ? null : soTien;
};

/**
 * Tách số tiền và kiểu tính (theo người / trọn gói cả lớp) từ chuỗi dự trù kinh phí
 * (vd: "600.000 đồng/ người" => { soTien: 600000, theoNguoi: true })
 */
const docKinhPhi = (text?: string): { soTien: number; theoNguoi: boolean } | null => {
    const soTien = laySoTien(text);
    if (soTien == null) return null;
    return { soTien, theoNguoi: /\/\s*(người|ng|hv|học viên)/i.test(text || '') };
};

/**
 * Gom các chi tiết đăng ký của cùng 1 lớp (lớp gốc + những người tham gia lớp đó) thành 1 dòng
 */
const gomTheoLop = (dataTable: Array<any>) => {
    const map = new Map<string, any>();

    (dataTable || []).forEach((item) => {
        // ưu tiên gom theo lớp gốc, nếu không có thông tin lớp thì gom theo nội dung đăng ký
        const idGoc = item.idChiTietGoc > 0
            ? item.idChiTietGoc
            : item.idChiTiet > 0 ? item.idChiTiet : 0;
        const key = idGoc > 0
            ? `lop-${idGoc}`
            : `nd-${item.chuyenDe}|${item.noiDung}|${item.coSoDaoTao}|${item.thoiGianDuKien}`;

        let lop = map.get(key);
        if (!lop) {
            lop = {
                key,
                idGoc,
                chuyenDe: item.chuyenDe,
                noiDung: item.noiDung,
                coSoDaoTao: item.coSoDaoTao,
                thoiGianDuKien: item.thoiGianDuKien,
                duTruKinhPhi: item.duTruKinhPhi,
                idLopDaoTao: 0,
                dsHocVien: [],
                dsDonVi: [] as Array<string>
            };
            map.set(key, lop);
        }

        lop.dsHocVien.push(item);
        if (!(lop.idLopDaoTao > 0) && item.idLopDaoTao > 0) {
            lop.idLopDaoTao = item.idLopDaoTao;
            lop.trangThaiLopDaoTao = item.trangThaiLopDaoTao;
        }
        if (item.tenDonVi && !lop.dsDonVi.includes(item.tenDonVi)) {
            lop.dsDonVi.push(item.tenDonVi);
        }
    });

    return Array.from(map.values()).map((lop, index) => {
        // đơn giá của lớp lấy theo dòng gốc (dòng gốc có thể không nằm trong danh sách => dùng duTruKinhPhiGoc server trả về),
        // không có thì lấy dòng đầu tiên đọc được số tiền
        const dongGoc = lop.dsHocVien.find((x: any) => x.idChiTiet === lop.idGoc);
        const textGoc: string | undefined = dongGoc?.duTruKinhPhi
            || lop.dsHocVien.find((x: any) => x.duTruKinhPhiGoc)?.duTruKinhPhiGoc;
        const kinhPhiGoc = docKinhPhi(textGoc)
            ?? lop.dsHocVien.map((x: any) => docKinhPhi(x.duTruKinhPhi)).find(Boolean);

        let tongKinhPhi: number | null = null;
        if (kinhPhiGoc) {
            tongKinhPhi = kinhPhiGoc.theoNguoi
                // giá theo người: mỗi người lấy số của mình, thiếu thì lấy đơn giá của lớp
                ? lop.dsHocVien.reduce((tong: number, x: any) =>
                    tong + (docKinhPhi(x.duTruKinhPhi)?.soTien ?? kinhPhiGoc.soTien), 0)
                // giá trọn gói cả lớp: không nhân theo số người
                : kinhPhiGoc.soTien;
        }

        return {
            ...lop,
            // dòng đầu tiên không có kinh phí thì hiển thị kinh phí gốc
            duTruKinhPhi: lop.duTruKinhPhi || textGoc,
            stt: index + 1,
            soNguoi: lop.dsHocVien.length,
            tongKinhPhi
        };
    });
};

const ViewDangKyDaoTaoTable = ({
    dataTable,
    selectedKeys,
    onSelectedChange,
    choPhepChon = choPhepChonMacDinh,
    isMoLop,
    coTheGop,
    onReload
}: {
    dataTable: Array<any>;
    selectedKeys?: Array<number>;
    onSelectedChange?: (dsChon: Array<any>) => void;
    /** nhân viên (chi tiết đăng ký) nào được hiện checkbox để chọn duyệt / từ chối */
    choPhepChon?: (hocVien: any) => boolean;
    isMoLop?: boolean;
    /** cho phép chọn các dòng để gộp thành 1 lớp */
    coTheGop?: boolean;
    onReload?: () => void
}) => {

    const navigate = useNavigate();

    const dispatch = useDispatch();
    const dataLop = useMemo(() => gomTheoLop(dataTable), [dataTable]);
    const dsHocVienTatCa = useMemo(
        () => dataLop.flatMap((lop: any) => lop.dsHocVien),
        [dataLop]
    );
    const dsHocVienChonDuoc = useMemo(
        () => dsHocVienTatCa.filter(choPhepChon),
        [dsHocVienTatCa, choPhepChon]
    );

    const [dsChonNoiBo, setDsChonNoiBo] = useState<Array<number>>([]);
    const dsChon = selectedKeys ?? dsChonNoiBo;
    const [showMoLop, setShowMoLop] = useState<any>({ isShow: false, data: {} });


    const handleKhoiTao = async () => {
        try {
            dispatch(showLoadingScreen());
            await khoiTaoLopDaoTaoKeHoach(showMoLop.data);
            toast.success('Khởi tạo lớp đào tạo thành công');
            onReload();
            setShowMoLop({
                isShow: false,
                data: {}
            })
        } catch (e: any) {
            toast.error(e.message || 'Có lỗi xảy ra');
        } finally {
            dispatch(hideLoadingScreen());
        }
    };
    // các dòng (lớp) đang chọn để gộp
    const [dsLopChonGop, setDsLopChonGop] = useState<Array<string>>([]);
    const [showGop, setShowGop] = useState<any>({ isShow: false, data: {} });
    // nhân viên đã có trong các dòng đang chọn gộp
    const dsNhanVienDaChonGop = useMemo(
        () => new Set(dataLop
            .filter((x: any) => dsLopChonGop.includes(x.key))
            .flatMap((x: any) => x.dsHocVien.map((hv: any) => hv.idNhanVien))),
        [dataLop, dsLopChonGop]
    );

    // đổi bộ dữ liệu (đổi năm/đợt đăng ký) thì bỏ các lựa chọn cũ
    useEffect(() => {
        if (selectedKeys == null) {
            setDsChonNoiBo([]);
        }
        setDsLopChonGop([]);
    }, [dataTable]);

    /**
     * Mở popup gộp: lấy dòng đầu tiên (theo thứ tự trong bảng) làm dữ liệu gốc
     */
    const moPopupGop = () => {
        const dsLopGop = dataLop.filter((x: any) => dsLopChonGop.includes(x.key));
        if (dsLopGop.length < 2) {
            toast.warning("Vui lòng chọn ít nhất 2 dòng để gộp");
            return;
        }
        const lopDau = dsLopGop[0];
        // chi tiết gốc của dòng đầu tiên; gốc không nằm trong danh sách thì lấy người đầu tiên của dòng
        const dongGoc = lopDau.dsHocVien.find((x: any) => x.idChiTiet > 0 && x.idChiTiet === lopDau.idGoc)
            || lopDau.dsHocVien.find((x: any) => x.idChiTiet > 0);
        setShowGop({
            isShow: true,
            showErrors: false,
            tenNguoiGoc: dongGoc.hoTen,
            soDong: dsLopGop.length,
            soNguoi: dsLopGop.reduce((tong: number, x: any) => tong + x.dsHocVien.length, 0),
            data: {
                idChiTietGoc: dongGoc.idChiTiet,
                idChiTiets: dsLopGop.flatMap((x: any) => x.dsHocVien)
                    .filter((x: any) => x.idChiTiet > 0)
                    .map((x: any) => x.idChiTiet),
                chuyenDe: dongGoc.chuyenDe,
                noiDung: dongGoc.noiDung,
                coSoDaoTao: dongGoc.coSoDaoTao,
                thoiGianDuKien: dongGoc.thoiGianDuKien,
                duTruKinhPhi: dongGoc.duTruKinhPhi
            }
        });
    };

    const doiThongTinGop = (truong: string, giaTri: string) => {
        setShowGop((prev: any) => ({ ...prev, data: { ...prev.data, [truong]: giaTri } }));
    };

    const handleGop = async () => {
        const { data } = showGop;
        const thieuThongTin = ['chuyenDe', 'noiDung', 'coSoDaoTao', 'thoiGianDuKien', 'duTruKinhPhi']
            .some(truong => !(data[truong] || '').trim());
        if (thieuThongTin) {
            setShowGop((prev: any) => ({ ...prev, showErrors: true }));
            toast.warning("Vui lòng nhập đầy đủ thông tin");
            return;
        }
        try {
            dispatch(showLoadingScreen());
            await gopLopDangKyDaoTao(data);
            toast.success("Gộp đăng ký đào tạo thành công");
            setShowGop({ isShow: false, data: {} });
            setDsLopChonGop([]);
            onReload?.();
        } catch (e: any) {
            toast.error(e.message || 'Có lỗi xảy ra');
        } finally {
            dispatch(hideLoadingScreen());
        }
    };

    const doiDsChon = (dsKeyMoi: Array<number>) => {
        if (selectedKeys == null) {
            setDsChonNoiBo(dsKeyMoi);
        }
        onSelectedChange?.(dsHocVienTatCa.filter((x: any) => dsKeyMoi.includes(layKeyHocVien(x))));
    };

    const chonHocVien = (hocVien: any, isChon: boolean) => {
        const key = layKeyHocVien(hocVien);
        doiDsChon(isChon ? [...dsChon, key] : dsChon.filter(x => x !== key));
    };

    const soDaChon = dsChon.length;
    const chonTatCa = dsHocVienChonDuoc.length > 0 && soDaChon === dsHocVienChonDuoc.length;

    const columns = [
        {
            title: "TT",
            dataIndex: "stt",
            align: "center" as const,
            width: 50,
            render: (value: number) => <span>{value}</span>
        },
        {
            title: (
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    {dsHocVienChonDuoc.length > 0 && (
                        <Checkbox
                            title="Chọn tất cả"
                            checked={chonTatCa}
                            indeterminate={soDaChon > 0 && !chonTatCa}
                            onChange={(e) => doiDsChon(e.target.checked ? dsHocVienChonDuoc.map(layKeyHocVien) : [])}
                        />
                    )}
                    <span>Đối tượng</span>
                </div>
            ),
            dataIndex: "dsHocVien",
            width: 220,
            render: (dsHocVien: Array<any>) => (
                <>
                    {dsHocVien.map((hocVien) => {
                        const { mau, chuThich } = layMauVaChuThichTrangThai(hocVien);
                        return (
                            <div key={`${hocVien.id}-${hocVien.idChiTiet}`} style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 4 }}>
                                {choPhepChon(hocVien) && (
                                    <Checkbox
                                        checked={dsChon.includes(layKeyHocVien(hocVien))}
                                        onChange={(e) => chonHocVien(hocVien, e.target.checked)}
                                    />
                                )}
                                <a
                                    title={chuThich
                                        ? `${chuThich} - Nhấn để xem chi tiết đăng ký của ${hocVien.hoTen}`
                                        : `Xem chi tiết đăng ký của ${hocVien.hoTen}`}
                                    href={Links.DangKyDaoTao.ChiTietCaNhan + "?id=" + hocVien.id}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{ fontWeight: 500, color: mau }}
                                >
                                    {hocVien.hoTen}
                                </a>
                            </div>
                        );
                    })}
                </>
            )
        },
        {
            title: "Chuyên đề đào tạo",
            dataIndex: "chuyenDe",
            width: 150,
            render: (text: string) => <span className="text-enter">{text}</span>
        },
        {
            title: "Nội dung đào tạo",
            dataIndex: "noiDung",
            width: 350,
            render: (text: string) => <span className="text-enter">{text}</span>
        },
        {
            title: "Cơ sở đào tạo",
            dataIndex: "coSoDaoTao",
            width: 160,
            render: (text: string) => <span className="text-enter">{text}</span>
        },
        {
            title: "Dự trù kinh phí",
            dataIndex: "tongKinhPhi",
            width: 160,
            align: "right" as const,
            render: (tongKinhPhi: number | null, record: any) => {
                // không tách được số tiền hoặc chỉ 1 người => hiển thị nguyên văn
                if (tongKinhPhi == null || record.soNguoi === 1) {
                    return <span className="text-enter">{record.duTruKinhPhi}</span>;
                }
                return (
                    <div style={{ fontWeight: 500 }}>
                        {tongKinhPhi.toLocaleString('vi-VN')} đồng/{record.soNguoi} người
                    </div>
                );
            }
        },
        {
            title: "Thời gian đào tạo",
            dataIndex: "thoiGianDuKien",
            width: 130,
            render: (text: string) => <span className="text-enter">{text}</span>
        },
        {
            title: "Phòng/Ban đăng ký",
            dataIndex: "dsDonVi",
            width: 200,
            render: (dsDonVi: Array<string>, record: any) => (
                <>
                    {dsDonVi.map((tenDonVi, index) => {
                        const soNguoi = (record.dsHocVien || []).filter((x: any) => x.tenDonVi === tenDonVi).length;
                        return (
                            <div key={tenDonVi} className="text-enter">
                                {tenDonVi} ({soNguoi}){index < dsDonVi.length - 1 ? "," : ""}
                            </div>
                        );
                    })}
                </>
            )
        },
        ...(isMoLop ? [
            {
                title: "Trạng thái mở lớp",
                dataIndex: "trangThaiLopDaoTao",
                width: 120,
                align: "center" as const,
                render: (value, record) => {
                return <TrangThaiLopDaoTaoTag value={value} />
                }
            },
            {
            title: "Thao tác",
            dataIndex: "idLopDaoTao",
            width: 110,
            align: "center" as const,
            render: (idLopDaoTao: number, record: any) => {
                const daMoLop = record.trangThaiLopDaoTao > trangThaiLopDaoTao.chuaKhoiTao;

                return daMoLop ? (
                    <Link title="Xem thông tin lớp đào tạo"
                        className="hrm-btn hrm-btn-sm btn-primary"
                        to={`${Links.DaoTao.Xem}?id=${idLopDaoTao}`}
                    >
                        Xem
                    </Link>
                ) : (
                    <Button
                            className="hrm-btn-warning"
                            disabled={!(idLopDaoTao > 0)}
                            title={
                                idLopDaoTao > 0
                                    ? "Mở lớp đào tạo"
                                    : "Chưa có lớp đào tạo tương ứng"
                            }
                            onClick={() => {
                                setShowMoLop({
                                    isShow: true,
                                    data: {
                                        idLopDaoTao: idLopDaoTao,
                                        chuyenDe: record.chuyenDe,
                                        idNVTheoDoi: undefined
                                    }
                                })
                            }
                        }
                    >
                        Mở lớp
                    </Button>
                );
            }
        }] : [
            {
                title: "Trạng thái",
                dataIndex: "dsHocVien",
                width: 120,
                align: "center" as const,
                render: (dsHocVien: Array<any>) => getTrangThaiStats(dsHocVien)
            },])
    ];

    const choPhepGop = !!coTheGop && !isMoLop;

    return (
        <>
            {
                choPhepGop &&
                <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 8, padding: 8 }}>
                    <span style={{ color: "#8c8c8c" }}>Chọn các dòng ở cột "Gộp", dòng đầu tiên được lấy làm dữ liệu gốc</span>
                    <Button
                        type="primary"
                        className="hrm-btn"
                        icon={<MergeCellsOutlined />}
                        disabled={dsLopChonGop.length < 2}
                        onClick={moPopupGop}
                    >
                        Gộp đăng ký{dsLopChonGop.length > 0 ? ` (${dsLopChonGop.length})` : ""}
                    </Button>
                </div>
            }
            <Table
            bordered
            size="middle"
            dataSource={dataLop}
            rowKey="key"
            rowSelection={choPhepGop ? {
                columnTitle: <span title="Chọn các dòng cần gộp">Gộp</span>,
                columnWidth: 50,
                hideSelectAll: true,
                selectedRowKeys: dsLopChonGop,
                onChange: (keys) => setDsLopChonGop(keys as Array<string>),
                getCheckboxProps: (record: any) => ({
                    // đã tạo lớp đào tạo, không có chi tiết đăng ký,
                    // hoặc có nhân viên đã nằm trong dòng khác đang chọn (mỗi nhân viên chỉ xuất hiện 1 lần trong lớp gộp)
                    disabled: record.idLopDaoTao > 0
                        || !record.dsHocVien.some((x: any) => x.idChiTiet > 0)
                        || (!dsLopChonGop.includes(record.key)
                            && record.dsHocVien.some((x: any) => dsNhanVienDaChonGop.has(x.idNhanVien)))
                })
            } : undefined}
            // đặt cột chọn gộp ở cuối bảng
            columns={choPhepGop ? [...columns, Table.SELECTION_COLUMN] : columns}
            locale={{ emptyText: "Không có dữ liệu" }}
            pagination={false}
            sticky
            scroll={{ x: 1200 }}
                rowClassName={(record, index) => {
                    return record?.idLopDaoTao === showMoLop.data?.idLopDaoTao ? "selected-row row-key-" : "";
                }}
        >
            </Table>
            <ModalTextForm
                show={showMoLop.isShow}
                title="Khởi tạo lớp đào tạo"
                width={850}
                handleClose={() =>
                    setShowMoLop({
                        isShow: false,
                        data: {}
                    }) }
                handleSave={handleKhoiTao}
            >
                <Row gutter={[16,16]}>
                    <Col span="24">
                        <span>Bạn có muốn khởi tạo lớp đào tạo <strong style={{ fontSize:20 }}>{showMoLop.data?.chuyenDe}</strong>? Hãy chọn chuyên viên theo dõi và bấm nút <strong> Xác nhận</strong></span>
                    </Col>
                    <Col span="8">
                        <label>Chọn chuyên viên theo dõi: </label>
                    </Col>
                    <Col span="16">
                        <NhanVienSelectBox
                            theoQuyen="Theo dõi đào tạo"
                            value={showMoLop.data?.idNVTheoDoi}
                            onChange={(e: any) => {
                                setShowMoLop(prev => ({
                                    ...prev,
                                    data: {
                                        ...prev.data,
                                        idNVTheoDoi: e.target.value
                                    }
                                }));
                            }}
                        />
                    </Col>
                </Row>
            </ModalTextForm>
            <ModalTextForm
                show={showGop.isShow}
                title="Gộp đăng ký đào tạo"
                size="lg"
                handleClose={() => setShowGop({ isShow: false, data: {} })}
                handleSave={handleGop}
            >
                <Row gutter={[16, 12]}>
                    <Col span={24}>
                        Gộp <strong>{showGop.soDong}</strong> dòng ({showGop.soNguoi} người) thành 1 lớp,
                        lấy đăng ký của <strong>{showGop.tenNguoiGoc}</strong> làm dữ liệu gốc.
                        Nội dung bên dưới sẽ được cập nhật cho tất cả người trong lớp.
                    </Col>
                    {[
                        { truong: 'chuyenDe', nhan: 'Chuyên đề đào tạo' },
                        { truong: 'noiDung', nhan: 'Nội dung đào tạo' },
                        { truong: 'coSoDaoTao', nhan: 'Cơ sở đào tạo' },
                        { truong: 'thoiGianDuKien', nhan: 'Thời gian đào tạo' },
                        { truong: 'duTruKinhPhi', nhan: 'Dự trù kinh phí' }
                    ].map(({ truong, nhan }) => (
                        <Col span={24} key={truong}>
                            <label>{nhan} <span className="text-red">*</span></label>
                            <Input.TextArea
                                autoSize={{ minRows: 1 }}
                                status={showGop.showErrors && !(showGop.data?.[truong] || '').trim() ? 'error' : ''}
                                value={showGop.data?.[truong]}
                                onChange={(e) => doiThongTinGop(truong, e.target.value)}
                            />
                        </Col>
                    ))}
                </Row>
            </ModalTextForm>
        </>
    );
};

export default ViewDangKyDaoTaoTable;
